/**
 * One workout, as `Builder planu - tydzien - programator.dc.html` draws it: a header, a scrolling
 * body of warm-up and main work, and the day's load module along the bottom.
 *
 * The mock's structural claims, all of which this reproduces:
 *
 * · **Every row is a photograph, a badge and a name.** The warm-up block and the main work are the
 *   same shape twice. A movement is recognised by its still long before its name is read at 12px in
 *   a 232px column. The warm-up rows carry NO muscle line — the mock's `warmExRows` draws none.
 * · **The warm-up is a class of work, not a badge.** Its own `--blue-soft` surface at radius 8, its
 *   own uppercase label, and a bordered `R` on every row.
 * · **A superset is one list position.** One number split across `2a` / `2b`, carried by the badge's
 *   own edge and ink in the ACCENT. No frame, no rail, no indent.
 * · **The load module is the card's footer.** A label, a 5px bar and a value on a `48px 1fr 30px`
 *   grid. No total and no percentage share: the mock's footer prints neither.
 *
 * ── The card is a LAYER, on BOTH canvases ────────────────────────────────────────────────────
 * See `box`. The column owns the mock's `cardSt` and this renders its `bodySt`. That was the week's
 * arrangement alone until 13 September 2026, when the sequence canvas came off its own mock: the
 * sequence mock's `cardSt` is the week's minus the branches a cycle column cannot take, and the
 * `.standalone` class that used to restore the chrome for the old tape was literally that same
 * `cardSt` re-declared on the wrong element. One box, owned by the column, on both.
 *
 * ── Three paths for the move, as view ADR §12 requires ───────────────────────────────────────
 * · **Pointer** — the card drags onto another column's box.
 * · **Keyboard** — the context menu's "Przestaw" lifts and closes onto the card, ←/→ walk the
 *   stops, Space drops, Escape cancels. Nothing reaches the store until the drop, so a cancelled lift
 *   leaves a byte-identical tree. It lifted from a six-dot grip in the header until 15 September
 *   2026, when the owner removed the grip: the whole card already drags, and the menu, reached by
 *   Shift+F10 from any control in the card, is where the lift starts now.
 * · **Menu** — the card's CONTEXT menu lists all seven weekdays and dispatches the same command. It
 *   also carries the two acts the mock draws no control for: "Programuj dzień" and the rename.
 *
 * The card itself decides none of it. It holds two pieces of state — where a keyboard lift is
 * pointed, and whether the rename field is open — and every word, target and gate arrives on the
 * view model.
 *
 * ── Focus comes BACK here when the dock closes ───────────────────────────────────────────────
 * The dock is opened from the card and has to return focus to it, and in fullscreen the whole canvas
 * is unmounted while it is open — so Base UI cannot restore focus to an element that does not exist
 * at that moment. The page remembers the day and hands `shouldTakeFocus` down; the ARTICLE claims
 * focus once per request, guarded by the dependency array. `tabIndex={-1}` makes it focusable by
 * script without adding a seventh tab stop to the week.
 */

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { BarbellGlyph, WarmupGlyph } from '~/shared/ui/ExerciseGlyphs';
import { useDraggableItem } from '~/shared/ui/dnd/useDraggableItem';
import { useKeyboardMove } from '~/shared/ui/dnd/useKeyboardMove';
import { muscleColour } from '~/shared/ui/muscleColour';
import photoBox from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import type { DayCardView, ExerciseRowView } from '../model/dayCardView';

import { SessionMenu, type SessionActionsProps } from './SessionMenu';
import styles from './SessionCard.module.css';

/**
 * Everything the card needs to move itself, and nothing else it could learn from.
 *
 * One object rather than six props because they are one capability: the card either can be moved —
 * in which case it needs all of them — or the plan is not editable and it needs none.
 */
export interface SessionMoveProps {
  /** The id of the element carrying the keyboard instruction, once for the whole canvas. */
  readonly hintId: string;
  /** "Sesja w ruchu". */
  readonly sourceLabel: string;
  readonly cancelledMessage: string;
  /**
   * Send this card to a stop, and say what happened.
   *
   * `coordinate` is whatever the stop carried — a weekday on the grid, a position on the tape — and
   * the card never learns which. That is what lets ONE card component serve both canvases.
   */
  readonly moveDay: (
    daySlotToken: string,
    coordinate: number,
    context: { readonly sessionName: string; readonly from: number },
  ) => void;
  readonly announce: (message: string) => void;
  /**
   * Tell the canvas which STOP a keyboard lift is pointed at — a 0-based index into the card's own
   * targets — so the column or tile there can light up. `null` when nothing is lifted.
   */
  readonly setPendingTarget: (index: number | null) => void;
}

/**
 * The id of the ONE rename hint on the screen.
 *
 * A module constant rather than a `useId` threaded down through `WeekPane` and `DayColumn`: exactly
 * one week pane renders at a time on this route, so one fixed id is unambiguous.
 */
export const RENAME_HINT_ID = 'plan-builder-rename-hint';

/**
 * The two glyphs a row falls back to when the catalogue has no photograph of the movement.
 *
 * The drawings live in `shared/ui/ExerciseGlyphs` since 9 September 2026 — the library rail's rows
 * draw the same barbell, and a feature may not import from a page. This is the choice between them,
 * which is the card's own and stays here.
 */
function RowGlyph({ isWarmup }: { readonly isWarmup: boolean }) {
  return isWarmup ? (
    <WarmupGlyph className={styles.glyph} />
  ) : (
    <BarbellGlyph className={styles.glyph} />
  );
}

/**
 * One row of either block: a badge, a 40×30 still, and a name — with the muscle line on main work
 * only, which is what the mock's two row builders differ by.
 */
function ExerciseRow({
  row,
  badge,
  ruled,
}: {
  readonly row: ExerciseRowView;
  /** The row's leading mark, already styled by its own block. */
  readonly badge: ReactNode;
  /** True on every main-work row but the first — the mock's `borderTop: i ? … : 'none'`. */
  readonly ruled: boolean;
}) {
  const tint = row.muscle === null ? null : muscleColour(row.muscle.group);
  return (
    <div
      className={`${String(styles.row)} ${String(
        row.isWarmup ? styles.warmRow : styles.rowMain,
      )} ${String(ruled ? styles.rowRuled : '')}`}
    >
      {badge}
      {row.coverUrl === null ? (
        // The mock's placeholder: the muscle's own hue at 14%, carrying the glyph. A warm-up row and
        // a row the catalogue could not attribute have no hue of their own — the warm-up takes the
        // structural blue, exactly as the mock's `warmExRows` does, and an unattributed row takes
        // the neutral ground rather than inventing a muscle group the library never answered with.
        <span
          className={`${String(styles.thumb)} ${String(styles.placeholder)} ${String(
            row.isWarmup || tint === null ? photoBox.photo : '',
          )}`}
          style={
            row.isWarmup
              ? { background: 'var(--warm-bg)', color: 'var(--warm-blue)' }
              : tint === null
                ? undefined
                : {
                    background: `color-mix(in srgb, ${tint} 14%, transparent)`,
                    color: tint,
                  }
          }
          aria-hidden="true"
        >
          <RowGlyph isWarmup={row.isWarmup} />
        </span>
      ) : (
        <img
          src={row.coverUrl}
          // Decorative: the movement's name is the next element, so a description here would make a
          // screen reader read the exercise twice.
          alt=""
          className={`${String(styles.thumb)} ${String(styles.photoThumb)} ${String(photoBox.photo)}`}
          loading="lazy"
          decoding="async"
        />
      )}
      <span className={styles.rowText}>
        {/* One line, ellipsised, with the full name on `title` — the mock's own treatment. */}
        <span className={styles.exercise} title={row.name}>
          {row.name}
        </span>
        {/* The mock draws the muscle line on MAIN work only; its warm-up rows are a badge, a still
         * and a name. */}
        {row.muscle != null && !row.isWarmup && (
          <span className={styles.chip}>
            <i
              className={styles.chipDot}
              style={{ background: muscleColour(row.muscle.group) }}
              aria-hidden="true"
            />
            {row.muscle.label}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * One of the two icon buttons in the card header — the mock's duplicate and its ×.
 *
 * One component for both because they are the same object twice: a name that carries the workout,
 * and a refusal that keeps the control focusable. **`aria-disabled`, never `disabled`** (view ADR
 * §12): a refused control that drops out of the tab order takes the explanation with it.
 */
function HeaderAction({
  className,
  label,
  reason,
  onSelect,
  children,
}: {
  readonly className: string;
  readonly label: string;
  readonly reason: string | null;
  readonly onSelect: () => void;
  readonly children: ReactNode;
}) {
  const reasonId = useId();
  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={label}
        aria-describedby={reason !== null ? reasonId : undefined}
        aria-disabled={reason !== null ? 'true' : undefined}
        onClick={(event) => {
          // The card's own press opens the day panel; this one does not, and the two are told apart
          // by the button stopping its press here.
          event.stopPropagation();
          if (reason === null) onSelect();
        }}
      >
        {children}
      </button>
      {reason !== null && (
        <span id={reasonId} className={styles.hidden}>
          {reason}
        </span>
      )}
    </>
  );
}

/** The mock's duplicate glyph: a 9.5px square lifted off a second one behind it. */
function DuplicateGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      className={styles.headActionGlyph}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="7" y="7" width="9.5" height="9.5" rx="2" />
      <path d="M13 4.6a2 2 0 0 0-1.6-1.1H5.5a2 2 0 0 0-2 2v6a2 2 0 0 0 1.1 1.6" />
    </svg>
  );
}

interface SessionCardProps {
  readonly day: DayCardView;
  /**
   * The COLUMN's card box — the mock's `cardSt`, on either canvas.
   *
   * The box owns every visible byte (the fill, the edge, the radius, the shadow, every state), this
   * card renders as its body layer, and the box is what the browser drags. Non-nullable since
   * 13 September 2026: both mocks put `draggable` on `cardSt`, and the sequence column now owns its
   * box exactly as `DayColumn` does.
   */
  readonly box: RefObject<HTMLElement | null>;
  /** Open the day dock on this day. `null` when the plan cannot be edited. */
  readonly onProgram: ((slotToken: string) => void) | null;
  /** True when the dock just closed on THIS day and focus is owed back to its entry point. */
  readonly shouldTakeFocus: boolean;
  /** Rename this day's workout, or `null` when the plan cannot be edited. */
  readonly onRename: ((slotToken: string, name: string) => void) | null;
  /** The move capability, or `null` on a plan that cannot be edited. See `SessionMoveProps`. */
  readonly move: SessionMoveProps | null;
  /**
   * Duplicating and deleting THIS day, or `null` on a plan that cannot be edited.
   *
   * A SECOND object beside `move`: they are different capabilities with different refusals — a phase
   * holding one training refuses the MOVE and permits the copy — and folding them together would
   * make one `null` speak for both.
   */
  readonly actions: DayCardActions | null;
}

/** What the canvas hands down: the words, and the two dispatches bound to one day. */
export interface DayCardActions {
  readonly view: SessionActionsProps['view'];
  readonly duplicate: (slotToken: string, name: string) => void;
  readonly requestDelete: (slotToken: string, name: string) => void;
}

export function SessionCard({
  day,
  box,
  onRename,
  onProgram,
  shouldTakeFocus,
  move,
  actions,
}: SessionCardProps) {
  const cardRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [draftName, setDraftName] = useState<string | null>(null);

  // The card's own refusal: a phase holding one training has nowhere to move it to. A running fork
  // closes no card (owner, 15 September 2026).
  const moveRefusal = move === null ? null : day.move.disabledReason;
  const canMove = move !== null && moveRefusal === null;

  // The load module's wait, on the shared 150ms/300ms gate — the same one the volume rail runs, and
  // the same read behind both, so the two never draw a skeleton on different frames. The module
  // forks on `gate.waiting`, not on `isPending`: swapping to the bars' answer the instant it lands
  // and then handing over 300ms later is the module entering twice.
  const gate = useSkeletonGate(day.load?.isPending === true);
  const reservedRows = Array.from({ length: day.load?.reservedRows ?? 0 }, (_, index) => index);

  // What the browser drags is the COLUMN's box, on both canvases, and the whole card starts it —
  // both mocks put `draggable` on `cardSt`. Restricting the gesture to the grip read as "drag and
  // drop does not work at all" to the first person who tried it, and the grip itself went on
  // 15 September 2026. The keyboard lift is bound to the ARTICLE, not to the drag adapter.
  //
  // The payload carries everything the pane's drop monitor needs to word the announcement, because
  // by the time the drop lands the tree has moved on.
  const drag = useDraggableItem({
    ref: box,
    data: {
      kind: 'session',
      daySlotToken: day.slotToken,
      sessionName: day.title,
      from: day.move.index,
    },
    canDrag: canMove,
  });

  const keyboard = useKeyboardMove({
    count: day.move.targets.length,
    index: day.move.index,
    axis: 'horizontal',
    canLift: canMove,
    onLift: (target) => {
      move?.setPendingTarget(target);
      move?.announce(day.move.liftedLabel);
    },
    onPreview: (target) => {
      const stop = day.move.targets[target];
      move?.setPendingTarget(target);
      if (stop !== undefined) move?.announce(stop.preview);
    },
    onCommit: (target) => {
      const stop = day.move.targets[target];
      if (stop === undefined) return;
      move?.moveDay(day.slotToken, stop.coordinate, {
        sessionName: day.title,
        from: day.move.index,
      });
    },
    onCancel: () => {
      move?.setPendingTarget(null);
      move?.announce(move.cancelledMessage);
    },
  });

  // Fires when the flag BECOMES true, and not again while it stays true — the dependency array is
  // the whole guard, so a trainer who closed the dock and tabbed away is not yanked back.
  useEffect(() => {
    if (shouldTakeFocus) cardRef.current?.focus();
  }, [shouldTakeFocus]);

  const closeRenameAndRestoreFocus = () => {
    setDraftName(null);
    // `requestAnimationFrame` rather than a bare `focus()`: the card has not re-rendered yet, so the
    // element this wants does not exist at the moment it runs.
    requestAnimationFrame(() => {
      cardRef.current?.focus();
    });
  };

  const commitRename = () => {
    if (draftName === null || onRename === null) return;
    const next = draftName.trim();
    closeRenameAndRestoreFocus();
    // Nothing to say. This is what keeps opening and closing the field from marking the plan
    // unsaved, and what stops the day's name being copied onto the session.
    if (next === (day.authoredTitle ?? '')) return;
    onRename(day.slotToken, next);
  };

  // A running index across the WHOLE main list, so a superset pair is divided from what precedes it
  // exactly as two singles would be. The mock's `exRows` counts over the flat array; our blocks are
  // grouped, so the count is kept here.
  let rowIndex = 0;

  const card = (
    <article
      ref={cardRef}
      className={styles.card}
      aria-label={day.title}
      /* Focusable by SCRIPT only — `useDayDock` hands focus back here when the dock closes. It adds
       * no tab stop: the card's own controls are what a keyboard user walks. */
      tabIndex={-1}
      data-dragging={drag.isDragging ? '' : undefined}
      data-lifted={keyboard.isLifted ? '' : undefined}
      /* The instruction while the card holds a lift, and only then: at rest the card is not a
       * control and its arrows do nothing. */
      aria-describedby={keyboard.isLifted ? move?.hintId : undefined}
      // A lift tabbed away from can no longer be steered, so it is dropped and announced.
      onBlur={keyboard.host.onBlur}
      /* The mock's own gesture: clicking the card opens the day panel. The keyboard and menu
       * equivalent is the context menu's first entry, because an `article` cannot be tabbed to and
       * must not become a `<button>` — one holding a heading, a text field and three controls is not
       * a control.
       *
       * TWO guards. A React PORTAL bubbles its events through the REACT tree rather than the DOM
       * tree, so a press inside the context-menu popup — which renders into `<body>` — arrives here
       * as a click on the card. Containment in this card's own DOM subtree is what tells them apart.
       * The second is the ordinary one: every control inside the card acts on its own. */
      onClick={(event) => {
        if (onProgram === null || draftName !== null) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (cardRef.current?.contains(target) !== true) return;
        if (target.closest('button, input') !== null) return;
        onProgram(day.slotToken);
      }}
      /* Shift+F10 opens the menu, because a MacBook keyboard has no ContextMenu key.
       *
       * Bound ONCE, here, rather than on each control inside: a keydown on either header icon or
       * in the rename field bubbles through the React tree to this element, and
       * `useKeyboardMove` absorbs only the keys it acts on. The event is re-dispatched as a real
       * `contextmenu` rather than reaching into Base UI's open state, so the keyboard path and the
       * right-click path are the same path — there is no second way in to keep in step.
       *
       * The anchor is the FOCUSED element's own box, which is what a browser does for the
       * ContextMenu key: the menu lands under the control the trainer was standing on. */
      onKeyDown={(event) => {
        // The lift's keys, answered only when the card ITSELF has focus — which is where the
        // menu's "Przestaw" puts it. A key in the rename field or on a header icon is theirs.
        keyboard.host.onKeyDown(event);
        if (move === null || event.key !== 'F10' || !event.shiftKey) return;
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        event.preventDefault();
        const box = target.getBoundingClientRect();
        target.dispatchEvent(
          new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: Math.round(box.left + box.width / 2),
            clientY: Math.round(box.bottom),
          }),
        );
      }}
    >
      {/* The dimmed source slot, drawn over the card rather than instead of it — the browser is
       * dragging this element and unmounting it would end the gesture. Hidden from assistive
       * technology: the canvas's live region already announces the pickup and every stop. */}
      {drag.isDragging && move !== null && (
        <div className={styles.sourceGhost} aria-hidden="true">
          <span className={styles.sourceLabel}>{move.sourceLabel}</span>
          <span className={styles.sourceName}>{day.title}</span>
        </div>
      )}

      <header className={styles.head}>
        <span className={styles.headText}>
          {draftName === null ? (
            <h3 className={styles.title} title={day.title} aria-describedby={RENAME_HINT_ID}>
              {day.title}
            </h3>
          ) : (
            <h3 className={styles.title}>
              <input
                ref={titleRef}
                className={styles.titleInput}
                value={draftName}
                aria-label={day.nameFieldLabel}
                // `PostSessionTemplateInput.Name` is `[MaxLength(120)]`. Enforced at the input,
                // where the trainer sees the field stop taking characters, not as a 400 after a
                // save.
                maxLength={day.nameMaxLength}
                autoFocus
                onChange={(event) => {
                  setDraftName(event.target.value);
                }}
                onBlur={commitRename}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitRename();
                  }
                  if (event.key === 'Escape') {
                    // An editor absorbs the key that closes it — the screen listens for shortcuts.
                    event.stopPropagation();
                    closeRenameAndRestoreFocus();
                  }
                }}
              />
            </h3>
          )}
          <span className={styles.meta}>{day.meta}</span>
          {/* A day with nothing in it will not survive the next save. Announced and drawn nowhere:
           * the mock has no picture of it. See the stylesheet's header for the cost. */}
          {day.unsavedNote != null && <span className={styles.hidden}>{day.unsavedNote}</span>}
        </span>

        {/* The mock's two icons. They exist only where the acts do: a read-only plan draws neither,
         * rather than two controls that refuse for a reason nobody can act on. */}
        {actions !== null && (
          <>
            <HeaderAction
              className={String(styles.headAction)}
              label={day.headerActions.duplicate}
              reason={actions.view.duplicateRefusal}
              onSelect={() => {
                actions.duplicate(day.slotToken, day.title);
              }}
            >
              <DuplicateGlyph />
            </HeaderAction>
            <HeaderAction
              className={String(styles.cross)}
              label={day.headerActions.delete}
              reason={null}
              onSelect={() => {
                actions.requestDelete(day.slotToken, day.title);
              }}
            >
              <span className={styles.headActionCross} aria-hidden="true">
                ×
              </span>
            </HeaderAction>
          </>
        )}
      </header>

      <div className={styles.body}>
        {/* A day with nothing in it draws the invitation rather than a void. `inline={false}` so it
         * takes the mark, the sentence and the column layout the rest of the app's empty states
         * use, and `tone="action"` because there IS something to do here — the card itself is the
         * control, so no button is drawn inside it. */}
        {day.emptyInvitation !== null && (
          <EmptyState
            className={styles.emptyDay}
            mark={<DumbbellMarkIcon />}
            tone="action"
            title={day.emptyInvitation}
          />
        )}

        {day.warmup != null && (
          <section className={styles.warmup}>
            <h4 className={styles.warmupTitle}>{day.warmup.title}</h4>
            {day.warmup.rows.map((row) => (
              <ExerciseRow
                key={row.key}
                row={row}
                ruled={false}
                badge={
                  <span className={`${String(styles.badge)} ${String(styles.badgeWarmup)}`}>
                    {day.warmup?.badge}
                  </span>
                }
              />
            ))}
          </section>
        )}

        {/* Labelled rather than headed: the mock draws no "PRACA GŁÓWNA" strip — its count moved into
         * the header's sub-line — but a list of a day's exercises still needs a name in the
         * accessibility tree, or it announces as an unlabelled list of seven items. */}
        <ol className={styles.exercises} aria-label={day.mainTitle}>
          {day.blocks.map((block) =>
            block.kind === 'single' ? (
              <li key={block.key} className={styles.position}>
                {/* No badge on a row with no ordinal, which is a warm-up a degenerate group left
                 * standing among the singles. It occupies no main-work position, so a numbered box
                 * beside it would claim to hold nothing. */}
                <ExerciseRow
                  row={block.row}
                  ruled={rowIndex++ > 0}
                  badge={
                    block.row.ordinal == null ? null : (
                      <span className={styles.badge}>{block.row.ordinal}</span>
                    )
                  }
                />
              </li>
            ) : (
              // A superset and a warm-up mini-group are the same treatment carrying a different
              // meaning — the accent on the badge, and the position number split across `2a` / `2b`.
              // The mock draws no frame and no rail; the word is still rendered, visually hidden, so
              // the pairing reaches a screen reader as language rather than as geometry.
              <li key={block.key} className={styles.position}>
                <p className={styles.hidden}>{block.label}</p>
                {block.rows.map((row) => (
                  <ExerciseRow
                    key={row.key}
                    row={row}
                    ruled={rowIndex++ > 0}
                    badge={
                      <span
                        className={`${String(styles.badge)} ${String(
                          block.kind === 'superset' ? styles.badgeBracket : styles.badgeWarmup,
                        )}`}
                      >
                        {row.ordinal}
                      </span>
                    }
                  />
                ))}
              </li>
            ),
          )}
        </ol>
      </div>

      {/* `aria-busy` keys on the GATE, not on the read (owner, 21 September 2026): the reserve and
        * its bars outlive the answer by the gate's minimum life, and a region still drawing
        * placeholder rows is still about to change. */}
      {day.load != null && (
        <section className={styles.load} aria-busy={gate.waiting || undefined}>
          <h4 className={styles.loadTitle}>{day.load.title}</h4>
          {/* The word beside `aria-busy`, on the same value, so the pair cannot disagree. The
            * model nulls the label the moment the read answers, so the sentence leaves up to
            * 300ms before the bars do; `aria-busy` carries the region on its own until then. */}
          {gate.waiting && day.load.pendingLabel !== null && (
            <span className={styles.hidden}>{day.load.pendingLabel}</span>
          )}
          {gate.waiting
            ? /* The reserve. Same `.loadRow` grid as a real row, so the box the module holds while it
               * waits is the box it holds afterwards by construction. The GATE hides the bars and
               * never the box — a fast answer draws no skeleton at all, and `reservedRows` is the
               * same three rows whether the read is open or not, so the reserve stands through the
               * bars' minimum life as well. */
              reservedRows.map((index) => (
                <div key={index} className={styles.loadRow} aria-hidden="true">
                  <i
                    className={`${String(styles.loadLabelBar)} ${gate.skeleton ? String(skel.bar) : ''}`}
                  />
                  <span className={styles.loadTrack} />
                  <i
                    className={`${String(styles.loadValueBar)} ${gate.skeleton ? String(skel.bar) : ''}`}
                  />
                </div>
              ))
            : day.load.rows.map((entry) => (
                <div key={entry.group} className={`${String(styles.loadRow)} ${gate.reveal}`}>
                  <span className={styles.loadLabel}>{entry.label}</span>
                  {/* Hidden from assistive technology: the group and its number are ordinary text on
                   * either side of it, so the bar would announce a row already read. */}
                  <span className={styles.loadTrack} aria-hidden="true">
                    <i
                      className={styles.loadFill}
                      style={{
                        width: `${String(entry.barPercent)}%`,
                        background: muscleColour(entry.group),
                      }}
                    />
                  </span>
                  <span className={styles.loadValue}>{entry.points}</span>
                </div>
              ))}
        </section>
      )}
    </article>
  );

  // A read-only plan has no acts to put in a menu, so the card is drawn bare rather than wrapped in
  // one that would open on an empty popup.
  if (move === null) return card;

  return (
    <SessionMenu
      trigger={card}
      program={
        onProgram === null
          ? null
          : {
              label: day.programLabel,
              onSelect: () => {
                onProgram(day.slotToken);
              },
            }
      }
      rename={
        onRename === null
          ? null
          : {
              label: day.renameLabel,
              onSelect: () => {
                // Opens on what was AUTHORED, not on what is displayed: starting from the "Trening
                // bez nazwy" placeholder would offer to save it as a real name on the first
                // keystroke.
                setDraftName(day.authoredTitle ?? '');
              },
            }
      }
      lift={
        move === null
          ? null
          : {
              label: day.move.liftEntryLabel,
              disabledReason: moveRefusal,
              host: cardRef,
              start: keyboard.lift,
            }
      }
      moveLabel={day.move.menuGroupLabel}
      targets={day.move.targets}
      disabledReason={moveRefusal}
      onMove={(coordinate) => {
        move.moveDay(day.slotToken, coordinate, {
          sessionName: day.title,
          from: day.move.index,
        });
      }}
      actions={
        actions === null
          ? null
          : {
              view: actions.view,
              onDuplicate: () => {
                actions.duplicate(day.slotToken, day.title);
              },
              onRequestDelete: () => {
                actions.requestDelete(day.slotToken, day.title);
              },
            }
      }
    />
  );
}
